import { useCallback, useState } from 'react';
import {
  deleteMarketingAssets,
  DeleteMarketingAssetsResponse,
  fetchAllMarketingAssets,
} from '@/lib/api';
import { MarketingAssetSchema } from '@/lib/types';
import { useDownload } from './useDownload';

const getAllFiles = (
  filesData?: MarketingAssetSchema[],
  filesFlatData?: MarketingAssetSchema[],
) => [...(filesData ?? []), ...(filesFlatData ?? [])];

export type NotDeletedFiles = {
  names: string[];
  requestedCount: number;
};

// Files the server skipped, by name - `null` when every file went.
const getNotDeletedFiles = (
  { skipped_ids }: DeleteMarketingAssetsResponse,
  requested: MarketingAssetSchema[],
  requestedCount: number,
): NotDeletedFiles | null => {
  if (!skipped_ids.length) return null;
  const namesById = new Map(
    requested.map((file) => [file.id, file.name]),
  );
  return {
    names: skipped_ids.map((id) => namesById.get(id) ?? String(id)),
    requestedCount,
  };
};

export const useMarketingAssetBulkActions = ({
  filesData,
  filesFlatData,
  onAfterMutation,
  deleteAllParams,
}: {
  filesData?: MarketingAssetSchema[];
  filesFlatData?: MarketingAssetSchema[];
  onAfterMutation?: () => void;
  // The filter Delete all acts on, including the files not loaded yet.
  deleteAllParams?: Parameters<typeof fetchAllMarketingAssets>[0];
}) => {
  const [selected, setSelected] = useState<number[]>([]);
  const [removeLoading, setRemoveLoading] = useState(false);
  const [removeModalOpen, setRemoveModalOpen] = useState(false);
  const [deleteAllModalOpen, setDeleteAllModalOpen] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [deleteAllError, setDeleteAllError] = useState(false);
  const [notDeletedFiles, setNotDeletedFiles] =
    useState<NotDeletedFiles | null>(null);
  const { download, loading: downloadLoading } = useDownload();

  const handleDownloadClick = useCallback(() => {
    if (!selected.length) return;

    const downloadParams: {
      category: 'marketing_assets';
      file_ids: number[];
      extension?: string;
      entity_name?: string;
    } = {
      category: 'marketing_assets',
      file_ids: selected,
    };

    if (selected.length === 1) {
      const selectedFile = getAllFiles(filesData, filesFlatData).find(
        (file) => file.id === selected[0],
      );
      if (selectedFile?.extension) {
        downloadParams.extension = selectedFile.extension;
      }
      if (selectedFile?.name) {
        downloadParams.entity_name = selectedFile.name;
      }
    }

    download(downloadParams);
  }, [download, filesData, filesFlatData, selected]);

  const handleRemoveClick = useCallback(() => {
    if (!selected.length) return;
    setRemoveModalOpen(true);
  }, [selected]);

  const handleRemoveCancel = useCallback(() => {
    setRemoveModalOpen(false);
  }, []);

  const handleRemoveConfirm = useCallback(async () => {
    setRemoveLoading(true);
    try {
      const response = await deleteMarketingAssets(selected);
      setNotDeletedFiles(
        getNotDeletedFiles(
          response,
          getAllFiles(filesData, filesFlatData),
          selected.length,
        ),
      );
      setSelected([]);
      setRemoveModalOpen(false);
      onAfterMutation?.();
    } finally {
      setRemoveLoading(false);
    }
  }, [filesData, filesFlatData, onAfterMutation, selected]);

  const handleDeleteAllClick = useCallback(() => {
    setDeleteAllError(false);
    setDeleteAllModalOpen(true);
  }, []);

  const handleDeleteAllCancel = useCallback(() => {
    setDeleteAllModalOpen(false);
  }, []);

  const handleDeleteAllConfirm = useCallback(async () => {
    if (!deleteAllParams) return;
    setDeleteAllLoading(true);
    setDeleteAllError(false);
    try {
      const files = await fetchAllMarketingAssets(deleteAllParams);
      const response = files.length
        ? await deleteMarketingAssets(files.map(({ id }) => id))
        : { deleted_ids: [], skipped_ids: [] };
      setNotDeletedFiles(
        getNotDeletedFiles(response, files, files.length),
      );
      setSelected([]);
      setDeleteAllModalOpen(false);
      onAfterMutation?.();
    } catch {
      setDeleteAllError(true);
    } finally {
      setDeleteAllLoading(false);
    }
  }, [deleteAllParams, onAfterMutation]);

  const dismissNotDeletedFiles = useCallback(() => {
    setNotDeletedFiles(null);
  }, []);

  return {
    selected,
    setSelected,
    handleDownloadClick,
    handleRemoveClick,
    handleRemoveCancel,
    handleRemoveConfirm,
    removeModalOpen,
    downloadLoading,
    removeLoading,
    handleDeleteAllClick,
    handleDeleteAllCancel,
    handleDeleteAllConfirm,
    deleteAllModalOpen,
    deleteAllLoading,
    deleteAllError,
    notDeletedFiles,
    dismissNotDeletedFiles,
  };
};
